import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Select,
  Upload,
  message,
  Space,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import FinancialReportDocService from "../../../services/FinancialReportDocService";
import FinancialYearService from "../../../services/FinancialYearService";

// Tag options for financial reports
const TAG_OPTIONS = [
  { value: "Financial Report", label: "आर्थिक अहवाल (Financial Report)" },
  { value: "Income Report", label: "उत्पन्न अहवाल (Income Report)" },
];

// Language options
const LANGUAGE_OPTIONS = [
  { value: "English", label: "English" },
  { value: "Marathi", label: "Marathi" },
];

const FinancialReportDoc = () => {
  const [list, setList] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await FinancialReportDocService.getAll();
      const data = res.data || [];
      data.sort((a, b) => a.id - b.id);
      setList(data);
    } catch (err) {
      console.error(err);
      message.error("Failed to load financial reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchYears = async () => {
    try {
      const res = await FinancialYearService.getFinancialYears();
      setYears(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load financial years");
    }
  };

  useEffect(() => {
    fetchData();
    fetchYears();
  }, []);

  const getYearName = useCallback(
    (id) => {
      const y = years.find((x) => x.id === id);
      const yearName = y ? y.name || y.year_name || id : id;
      return yearName ? `FY ${yearName}` : "-";
    },
    [years]
  );

  // Group data by financial year
  const groupedData = useMemo(() => {
    const groups = {};
    list.forEach((item) => {
      const fyId = item.financial_year;
      if (!groups[fyId]) {
        groups[fyId] = [];
      }
      groups[fyId].push(item);
    });
    return Object.entries(groups).map(([fyId, items]) => ({
      key: `fy-${fyId}`,
      fyId: Number(fyId),
      fyName: getYearName(Number(fyId)),
      items,
    }));
  }, [list, getYearName]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "Marathi" });
    setFileList([]);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      tag: record.tag,
      financial_year: record.financial_year,
      language: record.language || "Marathi",
    });
    // If there's an existing PDF, show it in the file list
    if (record.pdf_file) {
      const fileName = record.pdf_file.split("/").pop();
      setFileList([
        {
          uid: "-1",
          name: fileName,
          status: "done",
          url: record.pdf_file,
        },
      ]);
    } else {
      setFileList([]);
    }
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete financial report",
      content: "Are you sure you want to delete this report?",
      async onOk() {
        try {
          await FinancialReportDocService.delete(id);
          message.success("Deleted successfully");
          fetchData();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const onFinish = async (values) => {
    const formData = new FormData();
    formData.append("tag", values.tag);
    formData.append("financial_year", values.financial_year);
    formData.append("language", values.language || "Marathi");

    // Only append file if a new file was selected
    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append("pdf_file", fileList[0].originFileObj);
    }

    setLoading(true);
    try {
      if (editing) {
        await FinancialReportDocService.update(editing.id, formData);
        message.success("Updated successfully");
      } else {
        if (fileList.length === 0 || !fileList[0].originFileObj) {
          message.error("Please upload a PDF file");
          setLoading(false);
          return;
        }
        await FinancialReportDocService.create(formData);
        message.success("Created successfully");
      }
      setIsModalOpen(false);
      form.resetFields();
      setFileList([]);
      fetchData();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isPdf = file.type === "application/pdf";
      if (!isPdf) {
        message.error("You can only upload PDF files!");
        return false;
      }
      return false; // Prevent auto upload
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList.slice(-1)); // Keep only the last file
    },
    fileList,
    maxCount: 1,
    accept: ".pdf",
  };

  const columns = [
    { title: "Tag", dataIndex: "tag", key: "tag" },
    {
      title: "Financial Year",
      dataIndex: "financial_year",
      key: "financial_year",
      render: (v) => getYearName(v),
    },
    { title: "Language", dataIndex: "language", key: "language" },
    {
      title: "PDF",
      dataIndex: "pdf_file",
      key: "pdf_file",
      render: (pdf_file) =>
        pdf_file ? (
          <a href={pdf_file} target="_blank" rel="noopener noreferrer">
            <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 18 }} /> View
            PDF
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>Financial Report Documents</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Report
          </Button>
        </div>
      </div>

      {groupedData.length === 0 ? (
        <Table columns={columns} dataSource={[]} loading={loading} />
      ) : (
        groupedData.map((group) => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div
              style={{
                background: "linear-gradient(90deg, #001529 0%, #042035 100%)",
                padding: "12px 16px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "white", fontWeight: 600, fontSize: 16 }}>
                {group.fyName}
              </span>
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Total Reports: {group.items.length}
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={group.items}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              style={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}
            />
          </div>
        ))
      )}

      <Modal
        title={editing ? "Edit Financial Report" : "Add Financial Report"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setFileList([]);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ language: "Marathi" }}
        >
          <Form.Item
            name="pdf"
            label="PDF Document"
            rules={[{ required: !editing, message: "Please upload a PDF" }]}
          >
            <Upload {...uploadProps} listType="text">
              <Button icon={<UploadOutlined />}>Click to Upload PDF</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name="tag"
            label="Tag"
            rules={[{ required: true, message: "Please select a tag" }]}
          >
            <Select
              showSearch
              placeholder="Select tag"
              options={TAG_OPTIONS}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="financial_year"
            label="Financial Year"
            rules={[
              { required: true, message: "Please select financial year" },
            ]}
          >
            <Select
              showSearch
              placeholder="Select year"
              options={years.map((y) => ({
                label: `FY ${y.name || y.year_name}`,
                value: y.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="language" label="Language">
            <Select options={LANGUAGE_OPTIONS} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Financial Report Details"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {viewing && (
          <div>
            <p>
              <strong>ID:</strong> {viewing.id}
            </p>
            <p>
              <strong>Tag:</strong> {viewing.tag}
            </p>
            <p>
              <strong>Financial Year:</strong>{" "}
              {getYearName(viewing.financial_year)}
            </p>
            <p>
              <strong>Language:</strong> {viewing.language}
            </p>
            <p>
              <strong>PDF:</strong>{" "}
              {viewing.pdf_file ? (
                <a
                  href={viewing.pdf_file}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FilePdfOutlined style={{ color: "#ff4d4f" }} /> Open PDF
                </a>
              ) : (
                "-"
              )}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FinancialReportDoc;
